"use client";
import { useActionState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@simurgh/feathers/ui/button";
import {
	ModalBody,
	ModalContent,
	ModalHeader,
} from "@simurgh/feathers/ui/modal";
import { Input } from "@simurgh/feathers/ui/input";
import { Form } from "@simurgh/feathers/ui/form";
import { TABLE } from "../lib/shared";
import { DeckSchema, type Deck, type DeckIdentifiable } from "../model";
import { createAndSaveRecord } from "../lib/create";
import { addToast } from ":shared/lib/toast";
import { TextField } from "@simurgh/feathers/ui/text-field";
import { FieldError, Label } from "@simurgh/feathers/ui/field";
import { useDisclosure } from "@simurgh/feathers/lib/use-disclosure";
import { useTranslation } from "react-i18next";

type FormState = {
	success: boolean;
	error?: string;
	deck?: Deck;
};

type CreateRecordButtonProps = {
	onOptimisticCreate?: (deck: DeckIdentifiable) => void;
};

export function CreateRecordButton({
	onOptimisticCreate,
}: CreateRecordButtonProps = {}) {
	const queryClient = useQueryClient();
	const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
	const { t } = useTranslation();

	const [state, formAction, isPending] = useActionState(
		async (
			_previousState: FormState,
			formData: FormData,
		): Promise<FormState> => {
			const title = formData.get("title") as string;
			const description = formData.get("description") as string;

			// Validate using Zod schema
			const validation = DeckSchema.safeParse({ title, description });

			if (!validation.success) {
				const firstError = validation.error.issues[0];
				return {
					success: false,
					error: firstError?.message || t("messages.error.validationFailed"),
				};
			}

			try {
				// Optimistic update: add deck immediately with temporary ID
				if (onOptimisticCreate) {
					const optimisticDeck: DeckIdentifiable = {
						...validation.data,
						id: `temp-${Date.now()}`,
						createdAt: new Date().toISOString(),
						updatedAt: new Date().toISOString(),
					};
					onOptimisticCreate(optimisticDeck);
				}

				const response = await createAndSaveRecord(validation.data);

				queryClient.invalidateQueries({ queryKey: [TABLE] });
				addToast({
					title: `${response.title} created successfully`,
				});

				// Close modal on success
				onClose();

				return {
					success: true,
					deck: response,
				};
			} catch (error) {
				const message =
					(error as Error)?.message ||
					t("messages.error.createDeckFailed");
				addToast({
					title: t("modals.createDeck"),
					description: message,
					color: "danger",
				});
				return {
					success: false,
					error: message,
				};
			}
		},
		{ success: false },
	);

	return (
		<>
			<Button onPress={onOpen} intent="primary">
				{t("actions.create")} {TABLE}
			</Button>

			<ModalContent
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				isDismissable={!isPending}
			>
				<ModalHeader>{t("modals.createDeck")}</ModalHeader>
				<ModalBody className="text-base">
					<Form action={formAction} className="space-y-4">
						<TextField isRequired isDisabled={isPending}>
							<Label>{t("forms.labels.title")}</Label>
							<Input type="text" name="title" />
							<FieldError />
						</TextField>
						<TextField isRequired isDisabled={isPending}>
							<Label>{t("forms.labels.description")}</Label>
							<Input type="text" name="description" />
							<FieldError />
						</TextField>
						{state.error &&
							!state.error.includes("title") &&
							!state.error.includes("description") && (
								<p className="text-sm text-danger">{state.error}</p>
							)}
						<div className="flex items-center justify-end gap-2 w-full mt-4">
							<Button
								type="button"
								intent="secondary"
								onPress={onClose}
								isDisabled={isPending}
							>
								{t("actions.cancel")}
							</Button>
							<Button
								type="submit"
								intent="primary"
								isDisabled={isPending}
								isPending={isPending}
							>
								{t("actions.create")}
							</Button>
						</div>
					</Form>
				</ModalBody>
			</ModalContent>
		</>
	);
}
